import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}/common`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface UserRole {
  role: 'ADMIN' | 'MANAGER' | 'USER';
  organization_type: 'company' | 'team' | 'department';
  organization_id: string;
  organization_name: string;
  hierarchy: {
    company?: {
      id: string;
      name: string;
    };
    team?: {
      id: string;
      name: string;
    };
    department?: {
      id: string;
      name: string;
    };
  };
}

export interface UserPermissions {
  user: {
    id: string;
    email: string;
    full_name: string;
    is_superuser: boolean;
    is_staff: boolean;
  };
  roles: UserRole[];
  organizations: {
    companies: Array<{ id: string; name: string }>;
    teams: Array<{ id: string; name: string }>;
    departments: Array<{ id: string; name: string }>;
  };
  permissions: {
    can_create_risk: boolean;
    can_create_task: boolean;
    can_export: boolean;
    can_approve: boolean;
    can_assign: boolean;
    is_admin: boolean;
    is_manager: boolean;
  };
  model_permissions?: Record<string, string[]>;
}

export interface PermissionCheck {
  has_permission: boolean;
  user_role: 'ADMIN' | 'MANAGER' | 'USER' | null;
  action: string;
  object_type?: string;
  object_id?: string;
}

class PermissionService {
  private permissionsCache: UserPermissions | null = null;
  private cacheExpiry = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private permissionCheckCache: Map<string, { result: PermissionCheck; expiry: number }> = new Map();
  private readonly PERMISSION_CHECK_CACHE_DURATION = 60 * 1000; // 1 minute

  async getUserPermissions(forceRefresh = false): Promise<UserPermissions> {
    const now = Date.now();
    
    // Return cached permissions if still valid
    if (!forceRefresh && this.permissionsCache && now < this.cacheExpiry) {
      return this.permissionsCache;
    }

    const response = await api.get<UserPermissions>('/permissions/');
    this.permissionsCache = response.data;
    this.cacheExpiry = now + this.CACHE_DURATION;
    return response.data;
  }

  async checkPermission(
    action: string,
    objectType?: string,
    objectId?: string,
    context?: {
      company_id?: string;
      team_id?: string;
      department_id?: string;
    }
  ): Promise<PermissionCheck> {
    // Create cache key
    const cacheKey = `${action}:${objectType || ''}:${objectId || ''}:${JSON.stringify(context || {})}`;
    const now = Date.now();
    
    // Check cache first
    const cached = this.permissionCheckCache.get(cacheKey);
    if (cached && now < cached.expiry) {
      return cached.result;
    }
    
    try {
      const response = await api.post<PermissionCheck>('/check-permission/', {
        action,
        object_type: objectType,
        object_id: objectId,
        context,
      });
      
      // Cache the result
      this.permissionCheckCache.set(cacheKey, {
        result: response.data,
        expiry: now + this.PERMISSION_CHECK_CACHE_DURATION
      });
      
      // Clean up old cache entries
      if (this.permissionCheckCache.size > 100) {
        const entriesToDelete: string[] = [];
        this.permissionCheckCache.forEach((value, key) => {
          if (now > value.expiry) {
            entriesToDelete.push(key);
          }
        });
        entriesToDelete.forEach(key => this.permissionCheckCache.delete(key));
      }
      
      return response.data;
    } catch (error) {
      // Silent fail for permission checks - they're expected to fail sometimes
      const errorResult = {
        has_permission: false,
        user_role: null,
        action,
        object_type: objectType,
        object_id: objectId,
      };
      
      // Cache error result for a shorter duration
      this.permissionCheckCache.set(cacheKey, {
        result: errorResult,
        expiry: now + 10000 // 10 seconds
      });
      
      return errorResult;
    }
  }

  // Helper methods for common permission checks
  async canCreateRisk(): Promise<boolean> {
    const permissions = await this.getUserPermissions();
    return permissions.permissions.can_create_risk;
  }

  async canUpdateRisk(riskId: string): Promise<boolean> {
    const result = await this.checkPermission('update', 'risk', riskId);
    return result.has_permission;
  }

  async canDeleteRisk(riskId: string): Promise<boolean> {
    const result = await this.checkPermission('delete', 'risk', riskId);
    return result.has_permission;
  }

  async canCreateTask(): Promise<boolean> {
    const permissions = await this.getUserPermissions();
    return permissions.permissions.can_create_task;
  }

  async canUpdateTask(taskId: string): Promise<boolean> {
    const result = await this.checkPermission('update', 'task', taskId);
    return result.has_permission;
  }

  async canDeleteTask(taskId: string): Promise<boolean> {
    const result = await this.checkPermission('delete', 'task', taskId);
    return result.has_permission;
  }

  async canExport(): Promise<boolean> {
    const permissions = await this.getUserPermissions();
    return permissions.permissions.can_export;
  }

  async isAdmin(): Promise<boolean> {
    const permissions = await this.getUserPermissions();
    return permissions.permissions.is_admin;
  }

  async isManager(): Promise<boolean> {
    const permissions = await this.getUserPermissions();
    return permissions.permissions.is_manager;
  }

  async getUserRoleInOrganization(
    organizationType: 'company' | 'team' | 'department',
    organizationId: string
  ): Promise<'ADMIN' | 'MANAGER' | 'USER' | null> {
    const permissions = await this.getUserPermissions();
    const role = permissions.roles.find(
      (r) => r.organization_type === organizationType && 
             r.organization_id === organizationId
    );
    return role?.role || null;
  }

  // Clear cache when user logs out or permissions change
  clearCache(): void {
    this.permissionsCache = null;
    this.cacheExpiry = 0;
    this.permissionCheckCache.clear();
  }

  // Clear permission check cache only
  clearPermissionCheckCache(): void {
    this.permissionCheckCache.clear();
  }
}

const permissionService = new PermissionService();
export default permissionService;